"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { signIn, getSession } from "next-auth/react";
import { Suspense, useState } from "react";

export function SignInForm(props: {
  showGithub: boolean;
  showGoogle: boolean;
}) {
  const router = useRouter();
  const params = useSearchParams();
  const callbackUrl = params.get("callbackUrl") ?? "/dashboard";
  const err = params.get("error");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(
    err ? "Sign-in failed. Try again." : null,
  );

  async function onCredentials(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const res = await signIn("credentials", {
      email: email.trim(),
      password,
      redirect: false,
    });
    if (res?.error) {
      setError("Invalid email or password.");
      setSubmitting(false);
      return;
    }
    await getSession();
    router.push(callbackUrl);
    router.refresh();
    setSubmitting(false);
  }

  return (
    <div className="mx-auto max-w-md space-y-8 rounded-2xl border border-white/10 bg-black/20 p-8">
      {(props.showGithub || props.showGoogle) && (
        <div className="space-y-3">
          {props.showGithub && (
            <button
              type="button"
              onClick={() =>
                void signIn("github", { callbackUrl })
              }
              className="flex w-full items-center justify-center rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-sm font-medium text-white transition hover:bg-white/10"
            >
              Continue with GitHub
            </button>
          )}
          {props.showGoogle && (
            <button
              type="button"
              onClick={() =>
                void signIn("google", { callbackUrl })
              }
              className="flex w-full items-center justify-center rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-sm font-medium text-white transition hover:bg-white/10"
            >
              Continue with Google
            </button>
          )}
          <p className="text-center text-xs text-teal-200/50">or</p>
        </div>
      )}

      <form onSubmit={(ev) => void onCredentials(ev)} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-teal-200/80">
            Email
          </label>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-teal-200/80">
            Password
          </label>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white"
          />
        </div>
        {error && (
          <p className="text-sm text-amber-200/90" role="alert">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-xl bg-teal-400 px-4 py-3 font-semibold text-teal-950 hover:bg-teal-300 disabled:opacity-60"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <p className="text-center text-sm text-teal-200/70">
        No account?{" "}
        <Link href="/auth/register" className="text-teal-300 hover:text-teal-200">
          Register
        </Link>
      </p>
    </div>
  );
}

export function SignInFormWithSuspense(props: {
  showGithub: boolean;
  showGoogle: boolean;
}) {
  return (
    <Suspense
      fallback={<p className="text-teal-200/70">Loading sign-in…</p>}
    >
      <SignInForm {...props} />
    </Suspense>
  );
}
